import type { LegacyList } from '@mypoz/shared';

import { legacyApi } from '@/lib/legacy-api.js';

/**
 * Raised when `get_msgs` answers with anything but the payload it promised. Same split every
 * other feature's request error draws (`PackagesRequestError`, `OrdersRequestError`) — the
 * vendor's own message is Chinese prose from its own code table, useful in a log and wrong in
 * front of a customer.
 */
export class NotificationsRequestError extends Error {
  constructor(
    readonly endpoint: string,
    readonly code: string,
    message: string,
  ) {
    super(`${endpoint} failed (${code}): ${message}`);
    this.name = 'NotificationsRequestError';
  }
}

/**
 * Comfortably above the 103 rows a live account carries today (MYP-112) — high enough that one
 * request gets everything in practice, with the same one-more-request ceiling-hit fallback
 * `fetchOrderRows` uses for `order_list`, in case an account ever grows past it.
 */
const PAGE_CEILING = 200;

/**
 * Every notification on the account.
 *
 * Fetched whole rather than paginated against the network. `get_msgs` does not sort by date — a
 * full fetch comes back grouped by `type` instead (every PACKAGE row, then every ORDER row, and
 * so on — see `notifications-wire.ts`) — so presenting one correctly-ordered feed needs the
 * complete set before it can be sorted; there is no such thing as "the next chronological page"
 * to ask this endpoint for. `NotificationsProvider` does that sort once the whole set is in.
 *
 * `type`/`readflag` filter parameters exist on the endpoint but are deliberately not used here:
 * tested live, `type` filtered cleanly, but `readflag=N` came back with one read row mixed into
 * twenty-four unread ones against a true unread count of twenty-nine — leaky enough that
 * server-side filtering cannot be trusted. Filtering stays client-side over the complete set, the
 * same call `fetchPackageRows`/`fetchOrderRows` already make for their own endpoints' filters.
 *
 * Scoping — the thing MYP-70/MYP-112 both call a release blocker — was tested the same way: a
 * full unfiltered fetch against this account returns exactly 103 rows, every one of them this
 * account's own (matches account MP66184 / userid 16562). MYP-81's own "appears not user-scoped,
 * ~98k global rows" finding does not reproduce against the endpoint as it behaves today. That
 * doesn't retroactively clear MYP-81 — a different probe, environment or a since-fixed vendor bug
 * could equally explain the gap — but it does answer this ticket's own "confirm scoping before
 * shipping" condition: today, for this account, the feed is properly scoped. Worth noting for
 * MYP-75: this call sends no token at all (see `legacyApi`'s own doc comment) and was still
 * correctly scoped, so whatever *is* scoping it isn't the documented `token` field.
 */
export async function fetchNotificationRows(): Promise<readonly unknown[]> {
  const first = await legacyApi.call<LegacyList<unknown>>('get_msgs', {
    page: 1,
    limit: PAGE_CEILING,
  });
  if (!first.ok) throw new NotificationsRequestError('get_msgs', first.code, first.message);

  if (first.data.list.length < PAGE_CEILING) return first.data.list;

  // The ceiling was hit, so there may be more — one extra request for the rest, the same
  // one-row-of-slack reasoning `fetchOrderRows` applies against this vendor's own off-by-one
  // `count` quirks.
  const all = await legacyApi.call<LegacyList<unknown>>('get_msgs', {
    page: 1,
    limit: Math.max(first.data.count, PAGE_CEILING) + 1,
  });
  if (!all.ok) throw new NotificationsRequestError('get_msgs', all.code, all.message);

  return all.data.list;
}

/** What `set_msgs_read` marks: one message (`tid` + `type`), or every unread one of a `type`. */
export type MarkReadTarget =
  { readonly tid: string; readonly type: string } | { readonly type: string };

/**
 * `set_msgs_read` (MYP-70): marks messages read on the vendor, so the next `get_msgs` reports them
 * read on any device. Keyed by `tid` + `type` because `get_msgs` sends no message `id`.
 */
export async function markNotificationsRead(target: MarkReadTarget): Promise<void> {
  const result = await legacyApi.call<{ message: string }>('set_msgs_read', { ...target });
  if (!result.ok) {
    throw new NotificationsRequestError('set_msgs_read', result.code, result.message);
  }
}
